import { useState, useRef, useEffect, useCallback } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useFeedbackPrompt, ScoutFeedbackIndicator } from '@it-sce/ui'
import type { CortexChatMessage, CortexSession, CortexSessionMessage, CortexStreamEvent } from '../../services/api'
import { api } from '../../services/api'
import MarkdownMessage from '../MarkdownMessage'
import './AriaChatPanel.css'
import { scopedLocalStorage } from '../../lib/storage'

const STORAGE_KEY_CURRENT = 'aria_session_id_org'

const assistantName = 'Aria'
const assistantInitial = 'A'

// TQ #6981 (Confirm-Engine P3): Confirm-Card State, kommt aus dem
// `confirmation_required` Stream-Event. Siehe CortexPage.tsx fuer das Pendant.
interface PendingConfirm {
  tool: string
  reason: string
  instruction: string
  confirmId: string
}

interface PanelMessage extends CortexChatMessage {
  pendingConfirm?: PendingConfirm
  confirmState?: 'pending' | 'approving' | 'approved' | 'cancelled' | 'error'
  confirmError?: string
}

interface ConfirmCardProps {
  confirm: PendingConfirm
  state: NonNullable<PanelMessage['confirmState']>
  error?: string
  onApprove: () => void
  onCancel: () => void
}

function ConfirmCard({ confirm, state, error, onApprove, onCancel }: ConfirmCardProps) {
  if (state === 'approved') {
    return <div className="aria-confirm-result aria-confirm-result--ok">Aktion bestätigt und ausgeführt.</div>
  }
  if (state === 'cancelled') {
    return <div className="aria-confirm-result aria-confirm-result--cancel">Abgebrochen</div>
  }
  const busy = state === 'approving'
  return (
    <div className="aria-confirm-card">
      <div className="aria-confirm-card-title">Bestätigung erforderlich</div>
      <ul className="aria-confirm-details">
        <li><strong>Tool:</strong> <code>{confirm.tool}</code></li>
        {confirm.reason && <li>{confirm.reason}</li>}
      </ul>
      {confirm.instruction && <p className="aria-confirm-question">{confirm.instruction}</p>}
      {state === 'error' && error && <p className="aria-confirm-err">{error}</p>}
      <div className="aria-confirm-actions">
        <button className="aria-confirm-btn aria-confirm-btn--cancel" onClick={onCancel} disabled={busy}>
          Abbrechen
        </button>
        <button className="aria-confirm-btn aria-confirm-btn--ok" onClick={onApprove} disabled={busy}>
          {busy ? 'Wird ausgeführt…' : 'Bestätigen & ausführen'}
        </button>
      </div>
    </div>
  )
}

const KNOWN_ROLES: ReadonlyArray<CortexChatMessage['role']> = ['user', 'assistant']

function isDisplayableRole(role: CortexSessionMessage['role']): role is CortexChatMessage['role'] {
  return (KNOWN_ROLES as ReadonlyArray<string>).includes(role)
}

function formatRelativeDate(iso: string): string {
  try {
    const d = new Date(iso)
    const now = Date.now()
    const diff = (now - d.getTime()) / 1000
    if (diff < 60) return 'gerade eben'
    if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`
    if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`
    if (diff < 604800) return `vor ${Math.floor(diff / 86400)} Tagen`
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })
  } catch {
    return ''
  }
}

interface AriaChatPanelProps {
  /** TQ #5710: slug oder public_token der aktuellen Org — fuer den onboarding-status Endpoint. */
  slugOrToken?: string
  /** TQ #5710: true wenn der aktuelle Member im Onboarding-Mode ist (caller_onboarding_status='pending'). */
  onboardingPending?: boolean
  /** TQ #5710: Callback nach erfolgreichem PUT onboarding-status → status=completed. */
  onOnboardingCompleted?: () => void
}

export default function AriaChatPanel({ slugOrToken, onboardingPending = false, onOnboardingCompleted }: AriaChatPanelProps = {}) {
  const { token } = useAuth()
  const [open, setOpen] = useState(false)
  const [completing, setCompleting] = useState(false)

  // Proaktives Feedback-Prompt-System (F-4, Bundle #7268): Pulse am Button bei Prompt.
  // Waehrend Onboarding (Welcome-Modal offen) kein Pulse — Anti-Spam-Regel (Spec Abschnitt 5);
  // serverseitiger Poll-Guard greift zusaetzlich (F-2).
  const { pendingPrompt } = useFeedbackPrompt(token, open || onboardingPending)
  const [completeError, setCompleteError] = useState<string | null>(null)

  // Session-Liste + aktuelle Session
  const [sessions, setSessions] = useState<CortexSession[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [sessionsError, setSessionsError] = useState<string | null>(null)
  const [currentSessionId, setCurrentSessionIdState] = useState<string | undefined>(undefined)

  // Chat-State der aktuellen Session
  const [messages, setMessages] = useState<PanelMessage[]>([])
  const [restoring, setRestoring] = useState(false)
  const [restoreError, setRestoreError] = useState<string | null>(null)

  // Eingabe
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  // Stream-lokaler Abschluss-Marker: onClose laeuft im Closure von handleSend, dort ist
  // `loading` immer false. Nur die Ref sagt, ob der Stream sauber beendet wurde (TQ #1072).
  const streamFinishedRef = useRef(true)
  const [error, setError] = useState<string | null>(null)

  // TQ #6093 Streaming-State: ein dynamisches Status-Feld + die laufende
  // Assistant-Bubble. toolStatus zeigt was Aria gerade macht (Sucht Tools,
  // Ruft Tool auf, ...) — single Element, in-place update. streamingContent
  // ist der bisher empfangene Text der noch unfertigen Antwort, wird live
  // gerendert ohne dass eine neue Bubble pro tool-iteration aufpoppt.
  const [toolStatus, setToolStatus] = useState<string | null>(null)
  const [streamingContent, setStreamingContent] = useState<string>('')
  const streamingContentRef = useRef<string>('')
  const streamAbortRef = useRef<(() => void) | null>(null)

  // Sidebar (Sessions-Liste)
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [menuForSession, setMenuForSession] = useState<string | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const initRef = useRef(false)

  // TQ #6971: Storage-Key pro Tenant-Slug — globaler Key kollidierte ueber
  // Tenant-/Surface-Kontexte. Pro Org eigener Session-Pointer.
  const storageKey = `${STORAGE_KEY_CURRENT}::${slugOrToken || 'default'}`

  // Persist currentSessionId
  const setCurrentSessionId = useCallback((id: string | undefined) => {
    setCurrentSessionIdState(id)
    try {
      if (id) {
        scopedLocalStorage.setItem(storageKey, id)
      } else {
        scopedLocalStorage.removeItem(storageKey)
      }
    } catch {
      /* ignore */
    }
  }, [storageKey])

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 160)}px`
    }
  }, [input])

  useEffect(() => {
    if (open) {
      setTimeout(() => textareaRef.current?.focus(), 300)
    }
  }, [open])

  // TQ #5976: andere Komponenten oeffnen Aria via window-Event.
  //   window.dispatchEvent(new CustomEvent('aria:open', { detail: { prefill: '...' } }))
  // Optional `prefill` setzt den Input-Text, sodass z.B. die Tasks-Empty-State-
  // CTA "Mit Aria besprechen" einen Vorschlagstext mitgibt. Kein globaler Store
  // noetig, AriaChatPanel ist immer im DOM (Layout.tsx).
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail as { prefill?: string } | undefined
      setOpen(true)
      if (detail?.prefill) {
        setInput(detail.prefill)
      }
    }
    window.addEventListener('aria:open', handler)
    return () => window.removeEventListener('aria:open', handler)
  }, [])

  // Sessions vom Backend laden. TQ #6971: null = transienter Fehler (vs. [] =
  // wirklich keine Sessions), damit der Init-Effect den Pointer nicht wegwirft.
  const loadSessions = useCallback(async (): Promise<CortexSession[] | null> => {
    if (!token) return null
    api.setToken(token)
    setSessionsLoading(true)
    setSessionsError(null)
    try {
      const list = await api.listCortexSessions({ limit: 100 })
      // Sortiere nach last_message_at desc
      list.sort((a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime())
      setSessions(list)
      return list
    } catch (err) {
      console.error('[Aria] Failed to list sessions:', err)
      setSessionsError('Sessions konnten nicht geladen werden.')
      return null
    } finally {
      setSessionsLoading(false)
    }
  }, [token])

  // Messages einer Session laden. TQ #6971: true = geladen, false = weg/Fehler.
  const loadSessionMessages = useCallback(async (sessionId: string): Promise<boolean> => {
    setRestoring(true)
    setRestoreError(null)
    setMessages([])
    try {
      const result = await api.getCortexSessionMessages(sessionId)
      if (result === null) {
        setRestoreError('Diese Unterhaltung existiert nicht mehr.')
        return false
      }
      // TQ #6093: leere Assistant-Bubbles (intermediate tool-call iterations
      // ohne text content) sind UX-Laerm — Cortex persistiert sie aus
      // Audit-Gruenden, der User soll sie aber nicht als 'A'-Avatar sehen.
      const restored: CortexChatMessage[] = result
        .filter(m => isDisplayableRole(m.role))
        .map(m => ({ role: m.role as CortexChatMessage['role'], content: m.content }))
        .filter(m => m.role === 'user' || m.content.trim().length > 0)
      setMessages(restored)
      return true
    } catch (err) {
      console.error('[Aria] Failed to load messages:', err)
      setRestoreError('Unterhaltung konnte nicht geladen werden.')
      return false
    } finally {
      setRestoring(false)
    }
  }, [])

  // Initial-Setup: Sessions laden + aktive Session wiederherstellen
  useEffect(() => {
    if (!token || initRef.current) return
    initRef.current = true

    let storedId: string | null = null
    try {
      storedId = scopedLocalStorage.getItem(storageKey)
    } catch {
      storedId = null
    }

    // TQ #6971: Restore-on-Mount, das den gespeicherten Pointer respektiert.
    // - List-Fehler (null): Pointer NICHT loeschen, History direkt versuchen.
    // - Stored-Session restaurieren (auch jenseits der ersten 100 Sessions).
    // - Fallback auf neueste Session nur, wenn die Stored-Session wirklich weg ist.
    void (async () => {
      const list = await loadSessions()
      if (list === null) {
        if (storedId) {
          setCurrentSessionId(storedId)
          await loadSessionMessages(storedId)
        }
        return
      }
      if (storedId) {
        setCurrentSessionId(storedId)
        const ok = await loadSessionMessages(storedId)
        if (!ok && list[0] && list[0].id !== storedId) {
          setCurrentSessionId(list[0].id)
          await loadSessionMessages(list[0].id)
        }
      } else if (list[0]) {
        setCurrentSessionId(list[0].id)
        await loadSessionMessages(list[0].id)
      } else {
        // Keine Sessions → Auto-Create beim ersten Send (sessionId=undefined)
        setCurrentSessionId(undefined)
      }
    })()
  }, [token, storageKey, loadSessions, loadSessionMessages, setCurrentSessionId])

  const handleSelectSession = useCallback(async (sessionId: string) => {
    if (sessionId === currentSessionId) return
    setCurrentSessionId(sessionId)
    setInput('')
    setError(null)
    setMenuForSession(null)
    await loadSessionMessages(sessionId)
  }, [currentSessionId, loadSessionMessages, setCurrentSessionId])

  const handleNewSession = useCallback(async () => {
    if (!token) return
    api.setToken(token)
    setError(null)
    setInput('')
    setMenuForSession(null)
    try {
      const created = await api.createCortexSession({})
      setSessions(prev => [created, ...prev.filter(s => s.id !== created.id)])
      setCurrentSessionId(created.id)
      setMessages([])
      setRestoreError(null)
      setTimeout(() => textareaRef.current?.focus(), 50)
    } catch (err) {
      console.error('[Aria] Failed to create session:', err)
      setError('Neue Unterhaltung konnte nicht erstellt werden.')
    }
  }, [token, setCurrentSessionId])

  const handleRenameSession = useCallback(async (sessionId: string) => {
    setMenuForSession(null)
    const current = sessions.find(s => s.id === sessionId)
    const proposed = window.prompt('Neuer Titel:', current?.title ?? '')
    if (proposed == null) return
    const trimmed = proposed.trim()
    if (!trimmed) return
    try {
      const updated = await api.renameCortexSession(sessionId, trimmed)
      setSessions(prev => prev.map(s => s.id === sessionId ? updated : s))
    } catch (err) {
      console.error('[Aria] Failed to rename session:', err)
      setError('Umbenennen fehlgeschlagen.')
    }
  }, [sessions])

  const handleDeleteSession = useCallback(async (sessionId: string) => {
    setMenuForSession(null)
    const ok = window.confirm('Diese Unterhaltung wirklich löschen?')
    if (!ok) return
    try {
      await api.deleteCortexSession(sessionId)
      setSessions(prev => prev.filter(s => s.id !== sessionId))
      if (sessionId === currentSessionId) {
        // Auf neueste verbleibende Session umschalten oder leer lassen
        const remaining = sessions.filter(s => s.id !== sessionId)
        if (remaining.length > 0) {
          setCurrentSessionId(remaining[0].id)
          await loadSessionMessages(remaining[0].id)
        } else {
          setCurrentSessionId(undefined)
          setMessages([])
        }
      }
    } catch (err) {
      console.error('[Aria] Failed to delete session:', err)
      setError('Löschen fehlgeschlagen.')
    }
  }, [currentSessionId, sessions, loadSessionMessages, setCurrentSessionId])

  // TQ #5710: "Ich bin fertig"-Button. Setzt onboarding_status=completed,
  // App.tsx re-fetcht Org → Badge wechselt von "Onboarding" zu "Aria".
  const handleMarkOnboardingCompleted = useCallback(async () => {
    if (!slugOrToken || completing) return
    setCompleting(true)
    setCompleteError(null)
    try {
      await api.setMyOnboardingStatus(slugOrToken, 'completed')
      onOnboardingCompleted?.()
    } catch (err) {
      console.error('[Aria] Failed to mark onboarding completed:', err)
      setCompleteError('Konnte Onboarding-Status nicht aktualisieren.')
    } finally {
      setCompleting(false)
    }
  }, [slugOrToken, completing, onOnboardingCompleted])

  // TQ #6093: handleSend nutzt jetzt /api/v1/chat/stream (WebSocket) statt
  // dem REST /api/v1/chat. Vorteil: tool_start/tool_result/content Events
  // werden waehrend der Agent-Loop incremental geliefert, damit die UI eine
  // single Status-Bar zeigen kann ('Sucht Tools...', 'Rufe X auf...') statt
  // pro Iteration einer leeren Avatar-Bubble.
  const handleSend = () => {
    const trimmed = input.trim()
    if (!trimmed || loading) return

    setInput('')
    setError(null)

    const userMessage: CortexChatMessage = { role: 'user', content: trimmed }
    setMessages(prev => [...prev, userMessage])

    if (token) {
      api.setToken(token)
    }
    streamFinishedRef.current = false
    setLoading(true)
    setToolStatus('Aria denkt nach…')
    setStreamingContent('')
    streamingContentRef.current = ''

    let resolvedSessionId: string | undefined = currentSessionId
    let didReceiveContent = false

    let stream: { abort: () => void }
    try {
      stream = api.startCortexChatStream({
        message: trimmed,
        sessionId: currentSessionId,
        onEvent: (evt: CortexStreamEvent) => {
          switch (evt.type) {
            case 'session':
              resolvedSessionId = evt.session_id
              if (!currentSessionId) {
                setCurrentSessionId(evt.session_id)
                void loadSessions()
              }
              setToolStatus('Aria denkt nach…')
              break
            case 'info':
              setToolStatus(evt.message)
              break
            case 'thinking':
              setToolStatus('Aria denkt nach…')
              break
            case 'tool_start':
              if (evt.tool_name === 'tool_search') {
                const query = (evt.args as { query?: string })?.query?.trim()
                setToolStatus(query ? `Sucht Tools für „${query}“…` : 'Sucht passendes Tool…')
              } else {
                setToolStatus(`Rufe ${evt.tool_name} auf…`)
              }
              break
            case 'tool_result':
              // Result kommt typischerweise direkt vor dem nächsten content/tool_start —
              // wir lassen den Status stehen bis der nächste Event ihn überschreibt.
              setToolStatus(evt.is_error ? `${evt.tool_name}: Fehler` : `${evt.tool_name} fertig`)
              break
            case 'confirmation_required': {
              // TQ #6981: geparkter Tool-Call. Gestreamten Content promoten,
              // dann Confirm-Card-Bubble anhaengen.
              const streamed = streamingContentRef.current
              setMessages(prev => {
                const next: PanelMessage[] = [...prev]
                if (streamed.trim()) {
                  next.push({ role: 'assistant', content: streamed })
                }
                next.push({
                  role: 'assistant',
                  content: '',
                  pendingConfirm: {
                    tool: evt.tool,
                    reason: evt.reason,
                    instruction: evt.instruction,
                    confirmId: evt.confirm_id,
                  },
                  confirmState: 'pending',
                })
                return next
              })
              setStreamingContent('')
              streamingContentRef.current = ''
              setToolStatus(null)
              break
            }
            case 'content': {
              // Erstes content-Event: clear Status, start Live-Bubble
              if (!didReceiveContent) {
                didReceiveContent = true
                setToolStatus(null)
              }
              const next = streamingContentRef.current + evt.text
              streamingContentRef.current = next
              setStreamingContent(next)
              break
            }
            case 'done':
              streamFinishedRef.current = true
              // Finalize: streamingContent in messages-Liste promoten
              if (streamingContentRef.current) {
                const finalMsg: CortexChatMessage = {
                  role: 'assistant',
                  content: streamingContentRef.current,
                }
                setMessages(prev => [...prev, finalMsg])
              }
              setStreamingContent('')
              streamingContentRef.current = ''
              setToolStatus(null)
              setLoading(false)
              // Session in Sidebar hochziehen
              if (resolvedSessionId) {
                setSessions(prev => {
                  const sIdx = prev.findIndex(s => s.id === resolvedSessionId)
                  if (sIdx === -1) {
                    void loadSessions()
                    return prev
                  }
                  const updated = { ...prev[sIdx], last_message_at: new Date().toISOString() }
                  return [updated, ...prev.filter((_, i) => i !== sIdx)]
                })
              }
              break
            case 'error':
              streamFinishedRef.current = true
              console.error('[Aria stream] error:', evt.message)
              setError(evt.message || 'Verbindungsfehler. Bitte erneut versuchen.')
              setStreamingContent('')
              streamingContentRef.current = ''
              setToolStatus(null)
              setLoading(false)
              setMessages(prev => prev.filter(m => m !== userMessage))
              break
          }
        },
        onClose: (clean: boolean) => {
          streamAbortRef.current = null
          // Falls done/error nie kam (Verbindungsabbruch, abgelehnter Handshake), in den Fehlerzustand
          if (!streamFinishedRef.current) {
            streamFinishedRef.current = true
            if (!clean) {
              setError('Verbindung zur KI-Engine unterbrochen. Bitte erneut versuchen oder neu anmelden.')
              setMessages(prev => prev.filter(m => m !== userMessage))
            }
            setStreamingContent('')
            streamingContentRef.current = ''
            setToolStatus(null)
            setLoading(false)
          }
        },
      })
    } catch (e) {
      // z. B. SyntaxError/SecurityError bei new WebSocket — sonst bleibt 'Aria denkt nach…' stehen
      console.error('[Aria stream] Start fehlgeschlagen:', e)
      streamFinishedRef.current = true
      setError('Verbindung zur KI-Engine konnte nicht aufgebaut werden. Bitte neu anmelden.')
      setMessages(prev => prev.filter(m => m !== userMessage))
      setToolStatus(null)
      setLoading(false)
      return
    }
    streamAbortRef.current = stream.abort
  }

  // ─── Confirm-Engine P3 (TQ #6981) ────────────────────────────
  const handleConfirmApprove = useCallback(async (msgIndex: number) => {
    if (token) api.setToken(token)
    let confirmId: string | undefined
    setMessages(prev => prev.map((m, i) => {
      if (i === msgIndex && m.pendingConfirm) {
        confirmId = m.pendingConfirm.confirmId
        return { ...m, confirmState: 'approving', confirmError: undefined }
      }
      return m
    }))
    if (!confirmId) return
    try {
      const result = await api.approveCortexConfirmation(confirmId)
      setMessages(prev => {
        const next: PanelMessage[] = prev.map((m, i) =>
          i === msgIndex ? { ...m, confirmState: 'approved' as const } : m
        )
        if (result.content && result.content.trim()) {
          next.push({
            role: 'assistant',
            content: result.is_error ? `⚠️ ${result.content}` : result.content,
          })
        }
        return next
      })
    } catch (err) {
      const msg = (err as { error?: string })?.error || 'Bestätigung fehlgeschlagen.'
      setMessages(prev => prev.map((m, i) =>
        i === msgIndex ? { ...m, confirmState: 'error' as const, confirmError: msg } : m
      ))
    }
  }, [token])

  const handleConfirmCancel = useCallback(async (msgIndex: number) => {
    if (token) api.setToken(token)
    let confirmId: string | undefined
    setMessages(prev => {
      const target = prev[msgIndex]
      if (target?.pendingConfirm) confirmId = target.pendingConfirm.confirmId
      return prev.map((m, i) =>
        i === msgIndex ? { ...m, confirmState: 'cancelled' as const, confirmError: undefined } : m
      )
    })
    if (!confirmId) return
    try {
      await api.cancelCortexConfirmation(confirmId)
    } catch (err) {
      console.error('[Aria] cancel confirmation failed:', err)
    }
  }, [token])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <>
      {!open && (
        <button
          className="aria-toggle-btn"
          onClick={() => setOpen(true)}
          title={pendingPrompt ? `${assistantName} hat eine kurze Frage` : `${assistantName} öffnen`}
          aria-label={
            pendingPrompt
              ? `${assistantName} KI-Assistent öffnen (Feedback-Frage anstehend)`
              : `${assistantName} KI-Assistent öffnen`
          }
        >
          <ScoutFeedbackIndicator active={pendingPrompt !== null} />
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="22" height="22">
            <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            <path d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
          </svg>
          <span className="aria-toggle-label">{assistantName}</span>
        </button>
      )}

      <div
        className={`aria-panel ${open ? 'aria-panel--open' : ''} ${sidebarOpen ? 'aria-panel--with-sidebar' : ''}`}
        role="complementary"
        aria-label={`${assistantName} KI-Assistent`}
      >
        <div className="aria-panel-inner">
          {/* === Sessions-Sidebar === */}
          <aside className={`aria-sessions-sidebar ${sidebarOpen ? '' : 'aria-sessions-sidebar--collapsed'}`}>
            <div className="aria-sessions-header">
              <button
                className="aria-sessions-new-btn"
                onClick={handleNewSession}
                title="Neue Unterhaltung"
                disabled={!token}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                <span>Neue Session</span>
              </button>
            </div>

            <div className="aria-sessions-list">
              {sessionsLoading && sessions.length === 0 && (
                <div className="aria-sessions-empty">Lädt…</div>
              )}
              {!sessionsLoading && sessions.length === 0 && (
                <div className="aria-sessions-empty">Noch keine Sessions.</div>
              )}
              {sessions.map(s => (
                <div
                  key={s.id}
                  className={`aria-session-item ${s.id === currentSessionId ? 'aria-session-item--active' : ''}`}
                >
                  <button
                    className="aria-session-item-main"
                    onClick={() => handleSelectSession(s.id)}
                    title={s.title}
                  >
                    <span className="aria-session-title">{s.title || 'Neuer Chat'}</span>
                    <span className="aria-session-date">{formatRelativeDate(s.last_message_at)}</span>
                  </button>
                  <button
                    className="aria-session-menu-btn"
                    onClick={(e) => {
                      e.stopPropagation()
                      setMenuForSession(menuForSession === s.id ? null : s.id)
                    }}
                    title="Aktionen"
                    aria-label="Aktionen"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                      <circle cx="5" cy="12" r="1.5" />
                      <circle cx="12" cy="12" r="1.5" />
                      <circle cx="19" cy="12" r="1.5" />
                    </svg>
                  </button>
                  {menuForSession === s.id && (
                    <div className="aria-session-menu" onMouseLeave={() => setMenuForSession(null)}>
                      <button onClick={() => handleRenameSession(s.id)}>Umbenennen</button>
                      <button onClick={() => handleDeleteSession(s.id)} className="aria-session-menu-danger">Löschen</button>
                    </div>
                  )}
                </div>
              ))}
              {sessionsError && (
                <div className="aria-sessions-error">{sessionsError}</div>
              )}
            </div>
          </aside>

          {/* === Chat-Main === */}
          <section className="aria-chat-main">
            <div className="aria-panel-header">
              <div className="aria-panel-title">
                <button
                  className="aria-action-btn aria-sidebar-toggle"
                  onClick={() => setSidebarOpen(v => !v)}
                  title={sidebarOpen ? 'Sessions verbergen' : 'Sessions anzeigen'}
                  aria-label="Sessions umschalten"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                </button>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="18" height="18">
                  <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  <path d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                </svg>
                <span>{assistantName}</span>
              </div>
              <div className="aria-panel-actions">
                <button className="aria-action-btn" onClick={() => setOpen(false)} title="Schließen">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="aria-messages">
              {restoring && messages.length === 0 && (
                <div className="aria-message aria-message--assistant">
                  <div className="aria-message-avatar">{assistantInitial}</div>
                  <div className="aria-message-content aria-typing">
                    <span className="aria-dot" />
                    <span className="aria-dot" />
                    <span className="aria-dot" />
                  </div>
                </div>
              )}

              {messages.length === 0 && !loading && !restoring && (
                <div className="aria-welcome">
                  <p>Hallo! Ich bin Aria, Ihr KI-Assistent.</p>
                  <p>Ich helfe Ihnen mit Fragen zu Ihrer Organisation:</p>
                  <ul>
                    <li>„Welche Mitglieder haben Admin-Rechte?"</li>
                    <li>„Wie richte ich einen neuen Service ein?"</li>
                    <li>„Was ist in meinem aktuellen Abo enthalten?"</li>
                  </ul>
                </div>
              )}

              {/* TQ #5710: Mode-Switch Onboarding → Aria */}
              {onboardingPending && messages.length === 0 && !loading && !restoring && (
                <div className="aria-onboarding-finish">
                  <p>Sie befinden sich noch im Onboarding-Mode.</p>
                  <button
                    type="button"
                    className="aria-onboarding-finish-btn"
                    onClick={handleMarkOnboardingCompleted}
                    disabled={completing || !slugOrToken}
                  >
                    {completing ? 'Wird gespeichert…' : 'Ich bin fertig — Aria-Mode aktivieren'}
                  </button>
                  {completeError && (
                    <p className="aria-onboarding-finish-error">{completeError}</p>
                  )}
                </div>
              )}

              {messages.map((msg, index) => (
                <div key={index} className={`aria-message aria-message--${msg.role}`}>
                  <div className="aria-message-avatar">
                    {msg.role === 'user' ? 'Sie' : assistantInitial}
                  </div>
                  <div className="aria-message-content">
                    {msg.content && (
                      msg.role === 'assistant'
                        ? <MarkdownMessage content={msg.content} />
                        : msg.content
                    )}
                    {msg.pendingConfirm && msg.confirmState && (
                      <ConfirmCard
                        confirm={msg.pendingConfirm}
                        state={msg.confirmState}
                        error={msg.confirmError}
                        onApprove={() => handleConfirmApprove(index)}
                        onCancel={() => handleConfirmCancel(index)}
                      />
                    )}
                  </div>
                </div>
              ))}

              {/* TQ #6093: Live-Stream Bubble — wird waehrend content-Events
                  incremental gefuellt, ersetzt nicht mehrere leere Bubbles. */}
              {streamingContent && (
                <div className="aria-message aria-message--assistant">
                  <div className="aria-message-avatar">{assistantInitial}</div>
                  <div className="aria-message-content">
                    <MarkdownMessage content={streamingContent} />
                  </div>
                </div>
              )}

              {/* TQ #6093: Inline Status-Bar statt mehrerer leerer Avatar-
                  Bubbles. Single Element, in-place update via toolStatus.
                  Wird sichtbar wenn loading=true und (toolStatus gesetzt
                  ODER noch kein Streaming-Content empfangen). */}
              {loading && toolStatus && (
                <div className="aria-status-bar" role="status" aria-live="polite">
                  <span className="aria-status-spinner" aria-hidden="true">⚙️</span>
                  <span className="aria-status-text">{toolStatus}</span>
                </div>
              )}
              {loading && !toolStatus && !streamingContent && (
                <div className="aria-status-bar" role="status" aria-live="polite">
                  <span className="aria-status-text">Aria denkt nach…</span>
                </div>
              )}

              {error && (
                <div className="aria-error">{error}</div>
              )}

              {restoreError && !error && (
                <div className="aria-error">{restoreError}</div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="aria-input-area">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Frage an ${assistantName}… (Enter = Senden)`}
                rows={1}
                className="aria-textarea"
              />
              <button
                className="aria-send-btn"
                onClick={handleSend}
                disabled={loading || !input.trim()}
                title="Senden"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </section>
        </div>
      </div>
    </>
  )
}
